import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Dialog, DialogActions, DialogContent, Grid } from '@mui/material'
import i18n from 'simple-react-i18n'
import { isNil } from 'lodash'
import { DefaultDialogTitle } from '../../../components/styled/Dialog'
import SimpleDatePicker from '../../../components/forms/SimpleDatePicker'
import Select from '../../../components/forms/Select'
import SimpleTextArea from '../../../components/forms/SimpleTextArea'
import Input from '../../../components/forms/Input'
import { getFullDate } from '../../../utils/DateUtil'
import { rpqsEventShape } from '../../constants/RpqsShapes'

const RpqsEventDialog = ({
    open = false,
    event,
    contacts = [],
    managementUnits = [],
    canChooseManagementUnit = false,
    onClose = () => {},
    onValidate = () => {},
    onDelete = () => {},
}) => {
    const [editedEvent, setEditedEvent] = useState({})

    useEffect(() => {
        if (open) setEditedEvent(event ?? {})
    }, [open, event])

    const isNew = isNil(editedEvent.number)
    // Un événement de RPQS porte une année, contrairement à un événement de collectivité / EG.
    const isRpqsEvent = !isNil(editedEvent.year)

    return (
        <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
            <DefaultDialogTitle title={isNew ? i18n.newEvent : i18n.event} onClose={onClose} />
            <DialogContent>
                <Grid container spacing={1} sx={{ paddingTop: 1 }}>
                    <Grid size={4}>
                        <SimpleDatePicker
                            label={i18n.date}
                            value={editedEvent.date}
                            onChange={date => setEditedEvent(prev => ({ ...prev, date, hour: date }))}
                            withHour
                            obligatory
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            label={i18n.referent}
                            options={contacts}
                            value={editedEvent.contactCode}
                            nullLabel={i18n.none}
                            onChange={contactCode => setEditedEvent(prev => ({ ...prev, contactCode }))}
                        />
                    </Grid>
                    {isRpqsEvent && (
                        <Grid size={4}>
                            <Select
                                label={i18n.managementEntity}
                                options={managementUnits}
                                value={editedEvent.managementUnitId}
                                keyValue='id'
                                nullLabel={i18n.rpqsGlobalEvent}
                                onChange={managementUnitId => setEditedEvent(prev => ({ ...prev, managementUnitId }))}
                                disabled={!isNew || !canChooseManagementUnit}
                            />
                        </Grid>
                    )}
                    {!isNew && (
                        <>
                            <Grid size={4}>
                                <Input
                                    title={i18n.updateDate}
                                    value={getFullDate(editedEvent.updateDate)}
                                    disabled
                                />
                            </Grid>
                            <Grid size={4}>
                                <Input
                                    title={i18n.updateLogin}
                                    value={editedEvent.updateLogin}
                                    disabled
                                />
                            </Grid>
                        </>
                    )}
                    <Grid size={12}>
                        <SimpleTextArea
                            title={i18n.comment}
                            value={editedEvent.comment}
                            onChange={comment => setEditedEvent(prev => ({ ...prev, comment }))}
                            rows={6}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                {!isNew && (
                    <Button variant='contained' color='error' onClick={() => onDelete(editedEvent)}>
                        {i18n.delete}
                    </Button>
                )}
                <Button variant='contained' onClick={() => onValidate(editedEvent)} disabled={isNil(editedEvent.date)}>
                    {i18n.register}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

RpqsEventDialog.propTypes = {
    open: PropTypes.bool,
    event: rpqsEventShape,
    contacts: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
    managementUnits: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
    canChooseManagementUnit: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
    onDelete: PropTypes.func,
}

export default RpqsEventDialog
